import React, { useEffect, useState } from "react";
import { TrendingUp, PhoneMissed, PhoneCall, Users, Calendar, Sparkles, Clock, Lock, DollarSign, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Select, StatusBadge } from "@/components/ds";

const FILTERS = [
  { value: "today", label: "Today" }, { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" }, { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_90_days", label: "Last 90 Days" }, { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" }, { value: "all_time", label: "All Time" },
];
const ICONS: Record<string, any> = {
  calls_answered: PhoneCall, missed_calls: PhoneMissed, leads_captured: Users,
  appointments_booked: Calendar, opportunities_recovered: Sparkles,
  estimated_opportunity_value: DollarSign, revenue_influenced: DollarSign,
  after_hours_opportunities: Clock,
};
const money = (n: number) => `$${Number(n || 0).toLocaleString()}`;

export default function Opportunity() {
  const [filter, setFilter] = useState("last_30_days");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.opportunity({ date_filter: filter }).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="opportunity-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Missed Revenue &amp; Opportunity</h1>
          <p className="mt-1 text-sm text-slate-500">See the opportunities ResFil helps you capture. {data?.plan && <StatusBadge tone="blue">{data.plan} plan</StatusBadge>}</p>
        </div>
        <div className="w-full sm:w-52" data-testid="opp-date-filter">
          <Select value={filter} onChange={setFilter} options={FILTERS} testid="opp-filter-select" />
        </div>
      </div>

      {/* Revenue integrity banner */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="opp-revenue-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-xs text-amber-800">
          Values shown are <b>estimated opportunity value</b> and <b>revenue influenced</b> — not confirmed revenue.
          {data?.actual_revenue && !data.actual_revenue.available && <> Actual Revenue is unavailable: {data.actual_revenue.reason}</>}
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !data ? (
        <div className="text-sm text-red-600">Couldn't load opportunity data.</div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="opp-cards">
            {data.cards.map((c: any) => {
              const Icon = ICONS[c.key] || TrendingUp;
              return (
                <div key={c.key} className={`relative rounded-xl border p-4 shadow-sm ${c.locked ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={`opp-card-${c.key}`}>
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon className="h-4 w-4 text-blue-500" /> {c.label}
                  </div>
                  {c.locked ? (
                    <div className="mt-3" data-testid={`opp-locked-${c.key}`}>
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-400"><Lock className="h-3.5 w-3.5" /> Locked</div>
                      <p className="mt-1 text-xs text-blue-600">{c.upgrade_message}</p>
                    </div>
                  ) : (
                    <div className="mt-2 font-display text-2xl font-semibold text-slate-900">
                      {c.is_currency ? money(c.value) : (c.value ?? 0).toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pro sections */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="opp-service-breakdown">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Service breakdown &amp; recovery analytics</h2>
              {!data.pro_sections.unlocked && <StatusBadge tone="amber">Pro</StatusBadge>}
            </div>
            {data.pro_sections.unlocked ? (
              (data.pro_sections.service_breakdown?.length ?? 0) > 0 ? (
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-1">Service</th><th className="py-1">Avg value</th><th className="py-1">Type</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.pro_sections.service_breakdown.map((s: any) => (
                      <tr key={s.key}><td className="py-1.5">{s.service_name}</td><td className="py-1.5">{money(s.average_value)}</td><td className="py-1.5 capitalize text-slate-500">{s.kind}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-slate-500">No average job values defined yet. Add them under Services or via settings to power estimated opportunity value.</p>
              )
            ) : (
              <p className="text-xs text-blue-600">Upgrade to Pro to unlock per-service breakdowns, recovery analytics and after-hours value.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
